import { X } from 'lucide-react';
import {
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import startAudio from '@/assets/start.wav';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';

enum AudioState {
  Idle = 'Inactive',
  CreatingContext = 'Creating AudioContext',
  ContextCreated = 'AudioContext created, waiting interaction',
  ContextSuspendedInteractionRequired = 'AudioContext suspended, waiting interaction to resume',
  Resuming = 'Resuming AudioContext',
  StartingNodes = 'Starting audio nodes',
  Running = 'Playing 20Hz audio',
  Error = 'Error',
  Closed = 'AudioContext closed',
}

const Background20HzAudio = ({ children }: PropsWithChildren) => {
  const { t } = useTranslation('interactionNeededAlert');

  const audioContextRef = useRef<AudioContext | null>(null);
  const oscillatorRef = useRef<OscillatorNode | null>(null);
  const gainNodeRef = useRef<GainNode | null>(null);
  const isAudioSetup = useRef(false);

  const [interactionOccurred, setInteractionOccurred] = useState(false);
  const [audioStatus, setAudioStatus] = useState<AudioState>(AudioState.Idle);

  const setupAndStartAudio = useCallback(async () => {
    const audioContext = audioContextRef.current;

    if (!audioContext) {
      setAudioStatus(AudioState.Error);
      return;
    }

    if (audioContext.state === 'running' && isAudioSetup.current === true) {
      setAudioStatus(AudioState.Running);
      return;
    }

    setAudioStatus(AudioState.Resuming);

    try {
      await audioContext.resume();

      if (audioContext.state === 'running' && isAudioSetup.current === true) {
        setAudioStatus(AudioState.Running);
      } else if (
        audioContext.state === 'running' &&
        isAudioSetup.current === false
      ) {
        setAudioStatus(AudioState.StartingNodes);

        oscillatorRef.current?.disconnect();
        gainNodeRef.current?.disconnect();

        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();

        oscillator.type = 'sine';
        oscillator.frequency.setValueAtTime(20, audioContext.currentTime);
        gainNode.gain.setValueAtTime(0.001, audioContext.currentTime);
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        oscillator.start();

        oscillatorRef.current = oscillator;
        gainNodeRef.current = gainNode;
        isAudioSetup.current = true;

        setAudioStatus(AudioState.Running);
      }
    } catch (e) {
      console.error('setupAndStartAudio: Failed to resume AudioContext:', e);
      setAudioStatus(AudioState.ContextSuspendedInteractionRequired);
    }
  }, []);

  const tryResumeAudio = useCallback(async () => {
    const audioContext = audioContextRef.current;

    if (!audioContext) {
      return;
    }
    if (audioContext.state !== 'suspended') {
      return;
    }

    setAudioStatus(AudioState.Resuming);

    try {
      await audioContext.resume();
      if (!isAudioSetup.current) {
        setupAndStartAudio();
      }
    } catch (e) {
      console.error('tryResumeAudio: Failed to resume AudioContext:', e);
      setAudioStatus(AudioState.ContextSuspendedInteractionRequired);
    }
  }, [setupAndStartAudio]);

  useEffect(() => {
    setAudioStatus(AudioState.CreatingContext);
    let localAudioContext: AudioContext | null = null;

    try {
      localAudioContext = new AudioContext();
      audioContextRef.current = localAudioContext;

      setAudioStatus(
        localAudioContext.state === 'running'
          ? AudioState.Running
          : AudioState.ContextCreated
      );
    } catch (e) {
      console.error('Error creating AudioContext:', e);
      setAudioStatus(AudioState.Error);
      return;
    }

    const handleStateChange = () => {
      const audioContext = audioContextRef.current;

      if (!audioContext) {
        return;
      }

      const { state } = audioContext;

      switch (state) {
        case 'suspended':
          setAudioStatus(AudioState.ContextSuspendedInteractionRequired);
          break;
        case 'running':
          if (isAudioSetup.current === true) {
            setAudioStatus(AudioState.Running);
          } else {
            setAudioStatus(AudioState.StartingNodes);
          }
          break;
        case 'closed':
          setAudioStatus(AudioState.Closed);
          break;
      }
    };

    localAudioContext.addEventListener('statechange', handleStateChange);

    return () => {
      const audioContext = audioContextRef.current;

      if (!audioContext) {
        return;
      }

      audioContext.removeEventListener('statechange', handleStateChange);

      oscillatorRef.current?.stop();
      oscillatorRef.current?.disconnect();
      oscillatorRef.current = null;

      gainNodeRef.current?.disconnect();
      gainNodeRef.current = null;

      isAudioSetup.current = false;

      if (audioContext.state !== 'closed') {
        audioContext.close();
      }

      audioContextRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (interactionOccurred) {
      return;
    }

    const addListeners = () => {
      document.addEventListener('click', handleFirstInteraction);
      document.addEventListener('keydown', handleFirstInteraction);
      document.addEventListener('touchstart', handleFirstInteraction);
    };

    const removeListeners = () => {
      document.removeEventListener('click', handleFirstInteraction);
      document.removeEventListener('keydown', handleFirstInteraction);
      document.removeEventListener('touchstart', handleFirstInteraction);
    };

    const handleFirstInteraction = () => {
      setInteractionOccurred(true);
      setupAndStartAudio();
      removeListeners();
    };

    addListeners();

    return () => {
      removeListeners();
    };
  }, [interactionOccurred, setupAndStartAudio]);

  useEffect(() => {
    if (!interactionOccurred) {
      return;
    }

    const addListeners = () => {
      document.addEventListener('visibilitychange', handleVisibilityChange);
      document.addEventListener('mousedown', handleSubsequentInteraction);
      document.addEventListener('keydown', handleSubsequentInteraction);
    };

    const removeListeners = () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      document.removeEventListener('mousedown', handleSubsequentInteraction);
      document.removeEventListener('keydown', handleSubsequentInteraction);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        tryResumeAudio();
      }
    };

    const handleSubsequentInteraction = () => {
      tryResumeAudio();
    };

    addListeners();

    return () => {
      removeListeners();
    };
  }, [interactionOccurred, tryResumeAudio]);

  const statusColor = useMemo(() => {
    switch (audioStatus) {
      case AudioState.Running:
        return 'green';
      case AudioState.Error:
      case AudioState.Closed:
        return 'red';
      default:
        return 'orange';
    }
  }, [audioStatus]);

  return (
    <>
      {children}

      <div className="hidden">
        <h1>Anti-Throttle Audio Test (20Hz) with Auto-Resume</h1>
        <p>
          Clique/tecle para iniciar. O áudio tentará se manter ativo, resumindo
          automaticamente após suspensão quando a aba ficar visível ou houver
          nova interação.
        </p>
        <p>
          Status: <b style={{ color: statusColor }}>{audioStatus}</b>
        </p>
        <p>
          <small>
            AudioContext state (ref): {audioContextRef.current?.state ?? 'N/A'}
          </small>
        </p>
      </div>

      {interactionOccurred && <audio autoPlay src={startAudio} hidden />}

      <div
        className={`pointer-events-none absolute flex h-screen w-screen items-end px-2 pb-3 transition-opacity duration-300 ease-in-out ${
          !interactionOccurred ? 'opacity-100' : 'opacity-0'
        } `}
      >
        <Alert
          className={`shadow-xl ${!interactionOccurred ? 'pointer-events-auto' : 'pointer-events-none'}`}
        >
          <AlertTitle className="flex items-center justify-between">
            {t('title')}
            <Button
              variant="ghost"
              size="icon"
              className="h-5 w-5"
              title={t('closeButtonTitle')}
              disabled={interactionOccurred}
            >
              <X size={14} className="cursor-pointer" />
            </Button>
          </AlertTitle>
          <AlertDescription className="text-xs">
            {t('message')}
          </AlertDescription>
        </Alert>
      </div>
    </>
  );
};

export default Background20HzAudio;
